/** Zentrale Festwerte. Alles, was an mehr als einer Stelle gebraucht wird. */

export const REGION = 'eu-central-1' as const;
export const ZEITZONE = 'Europe/Berlin' as const;
export const LOCALE = 'de-DE' as const;

/** Cognito-Gruppen. Reihenfolge = absteigende Rechte. */
export const GRUPPEN = ['Admin', 'Erfasser'] as const;
export type Gruppe = (typeof GRUPPEN)[number];

export const PROTOKOLL_STATUS = ['DRAFT', 'SIGNED', 'RENDERED', 'SENT', 'STORNIERT'] as const;
export type ProtocolStatus = (typeof PROTOKOLL_STATUS)[number];

/** Ab `SIGNED` sind `data` und `dataHash` unveränderlich (CLAUDE.md §2). */
export const UNVERAENDERLICHE_STATUS: readonly ProtocolStatus[] = [
  'SIGNED',
  'RENDERED',
  'SENT',
] as const;

export const istUnveraenderlich = (status: ProtocolStatus | null | undefined): boolean =>
  status !== null && status !== undefined && UNVERAENDERLICHE_STATUS.includes(status);

export const PERSONEN_TYPEN = ['MIETER', 'VERMIETER', 'ZEUGE', 'SONSTIGE'] as const;
export type PersonTyp = (typeof PERSONEN_TYPEN)[number];

/**
 * Nutzungsart einer Einheit. Ein Objekt ist das Gebäude, eine Einheit die
 * Wohnung oder Gewerbefläche darin — übergeben wird immer eine Einheit.
 */
export const EINHEIT_ARTEN = ['WOHNUNG', 'GEWERBE', 'STELLPLATZ', 'SONSTIGE'] as const;
export type EinheitArt = (typeof EINHEIT_ARTEN)[number];

export const EINHEIT_ART_LABEL: Record<EinheitArt, string> = {
  WOHNUNG: 'Wohnung',
  GEWERBE: 'Gewerbeeinheit',
  STELLPLATZ: 'Stellplatz',
  SONSTIGE: 'Sonstige',
};

export const DOKUMENT_TYPEN = [
  'PROTOKOLL',
  'FOTO',
  'NOTIZ',
  'VERTRAG',
  'RECHNUNG',
  'SONSTIGE',
] as const;
export type DokumentTyp = (typeof DOKUMENT_TYPEN)[number];

export const DOKUMENT_TYP_LABEL: Record<DokumentTyp, string> = {
  PROTOKOLL: 'Protokoll',
  FOTO: 'Foto',
  NOTIZ: 'Notiz',
  VERTRAG: 'Vertrag',
  RECHNUNG: 'Rechnung',
  SONSTIGE: 'Sonstiges',
};

export const PROTOKOLL_STATUS_LABEL: Record<ProtocolStatus, string> = {
  DRAFT: 'Entwurf',
  SIGNED: 'Unterschrieben',
  RENDERED: 'PDF erstellt',
  SENT: 'Versendet',
  STORNIERT: 'Storniert',
};

export const PERSON_TYP_LABEL: Record<PersonTyp, string> = {
  MIETER: 'Mieter',
  VERMIETER: 'Vermieter',
  ZEUGE: 'Zeuge',
  SONSTIGE: 'Sonstige',
};

/** Auditierte Aktionen. Append-only (CLAUDE.md §3.6). */
export const AUDIT_AKTIONEN = [
  'LOGIN',
  'LOGOUT',
  'PROTOKOLL_ANGELEGT',
  'PROTOKOLL_SIGNIERT',
  /** Ein unterbrochener Abschluss wurde nachgeholt — Nummer und Unterschriften. */
  'ABSCHLUSS_NACHGEHOLT',
  'PROTOKOLL_STORNIERT',
  'PROTOKOLL_KORREKTUR',
  'PDF_ERZEUGT',
  /** Interner Abruf des PDFs aus der App — ohne Mail und ohne Download-Link. */
  'PDF_HERUNTERGELADEN',
  'MAIL_VERSENDET',
  'MAIL_BOUNCE',
  'MAIL_BESCHWERDE',
  'LINK_ABGERUFEN',
  'LINK_WIDERRUFEN',
  'LINK_ERNEUERT',
  'DOKUMENT_HOCHGELADEN',
  'DOKUMENT_GELOESCHT',
  'IMPORT_DURCHGEFUEHRT',
  'TEMPLATE_VERSION_ERSTELLT',
  'AUFBEWAHRUNG_MARKIERT',
  'LOESCHUNG_DURCHGEFUEHRT',
  /** Endgültige Löschung durch einen Admin — kein Rumpfdatensatz bleibt zurück. */
  'DATENSATZ_HART_GELOESCHT',
  'BETROFFENEN_EXPORT',
  'NUTZER_ANGELEGT',
  'NUTZER_DEAKTIVIERT',
] as const;
export type AuditAktion = (typeof AUDIT_AKTIONEN)[number];

/**
 * Logo im Branding. Zulässig sind nur PNG und JPEG — beides kann Chromium ohne
 * externe Abhängigkeit rendern.
 *
 * Das Logo liegt als Data-URI im Branding der Vorlagenversion, damit das PDF
 * ohne Netzzugriff gerendert werden kann. Ein DynamoDB-Datensatz darf 400 KB
 * groß sein; die Grenze hier hält das Logo mit Abstand darunter, sonst wäre
 * irgendwann die Vorlage nicht mehr speicherbar.
 */
export const LOGO_MIME_TYPEN = ['image/png', 'image/jpeg'] as const;
export const LOGO_MAX_KANTE_PX = 600;
export const LOGO_MAX_ZEICHEN = 150_000;

/** Offline-Grenzen (SPEC Kap. 4). */
export const FOTO_MAX_KANTE_PX = 2560;
export const FOTO_JPEG_QUALITAET = 0.8;
export const SPEICHER_WARNUNG_ANTEIL = 0.8;
export const SPEICHER_SPERRE_ANTEIL = 0.95;

/** Outbox-Backoff: 1s → 2s → 4s … gedeckelt bei 5 Minuten. */
export const OUTBOX_BACKOFF_BASIS_MS = 1_000;
export const OUTBOX_BACKOFF_MAX_MS = 5 * 60 * 1_000;
export const OUTBOX_MAX_VERSUCHE = 12;

/** Pull-Fenster beim Login/Reconnect: Protokoll-Metadaten der letzten 90 Tage. */
export const PULL_FENSTER_TAGE = 90;

/** Download-Links. */
export const DOWNLOAD_GUELTIGKEIT_TAGE_STANDARD = 7;
export const DOWNLOAD_GUELTIGKEIT_TAGE_MIN = 1;
export const DOWNLOAD_GUELTIGKEIT_TAGE_MAX = 30;
export const PRESIGNED_URL_LAUFZEIT_SEKUNDEN = 300;

/** Aufbewahrung. */
export const AUFBEWAHRUNG_MONATE_STANDARD = 36;
export const AUDIT_AUFBEWAHRUNG_JAHRE = 6;

/** S3-Pfadpräfixe (PHASENPLAN 0.5). */
export const S3_PRAEFIX = {
  objekte: 'objekte',
  protokolle: 'protokolle',
  templates: 'templates',
} as const;

/** Mindestgröße für Touch-Ziele in Pixeln (SPEC Kap. 12). */
export const TOUCH_ZIEL_MIN_PX = 44;
